import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { CustomTemplateListViewData } from '../types';
import { render } from 'lit';
import { GzPaginationWebComponent } from '../../../grid/uiElements/gzPagination';
import { GridSummaryWebComponent } from '../../../grid/uiElements/gridSummary';
import { translateFn } from '../../../utils/translator.helper';
import {ElementClass} from "../../../utils/htmlElement.utils";
import {TemplateState} from "../../../types/common/types";

export class GzCustomTemplateListView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$gridStates = this.shadow.querySelector('.grid-states');
    this.dom.$content = this.shadow.querySelector('.content');
    this.dom.$items = this.shadow.querySelector('.items');
    this.dom.$summary = this.shadow.querySelector('gz-grid-summary') as GridSummaryWebComponent;
    this.dom.$pagination = this.shadow.querySelector('gz-pagination') as GzPaginationWebComponent;
    this.dom.$noResultsContainer = this.shadow.querySelector('.no-results');
    this.dom.$noResultsMessage = this.shadow.querySelector('.no-results--message');
  }

  buildSuccessView(data: CustomTemplateListViewData) {
    if (!data) {
      this.dom.$wrapper.innerHTML = '';
      return;
    }
    this.setTranslatedStrings();
    this.renderItems(data);
    this.populateFooter(data);
    this.handleGridVisibility(data.itemsTotal);
  }

  private renderItems(data: CustomTemplateListViewData) {
    this.dom.$items.innerHTML = '';
    let lastRowContainer: HTMLDivElement;
    data.items.forEach((item, index) => {
      if ((data.columns === 0 && index === 0) || (data.columns > 0 && index % data.columns === 0)) {
        lastRowContainer = this.createRowElement(data.gap);
        this.dom.$items.appendChild(lastRowContainer);
      }
      const itemContainer = this.createItemElement(data.columns, data.gap);
      render(data.itemRenderer(item), itemContainer);
      lastRowContainer.appendChild(itemContainer);
    });
  }

  private createRowElement(gap: number): HTMLDivElement {
    const rowElement = document.createElement('div');
    rowElement.classList.add('row');
    rowElement.style.gap = `${gap}px`;
    rowElement.style.marginBottom = `${gap}px`;
    return rowElement;
  }

  private createItemElement(columns: number, gap: number): HTMLDivElement {
    const itemElement = document.createElement('div');
    itemElement.classList.add('item');
    const width = columns > 0 ? 100 / columns : 100;
    const itemGap = columns > 0 ? (gap * (columns - 1)) / columns : 0;
    itemElement.style.width = `calc(${width}% - ${itemGap}px)`;
    return itemElement;
  }

  private populateFooter(data: CustomTemplateListViewData) {
    const totalNumberOfPages = Math.ceil(data.itemsTotal / data.pageSize);
    this.dom.$pagination.totalNumberOfPages = totalNumberOfPages;
    this.dom.$pagination.pageSize = data.pageSize;
    this.dom.$pagination.currentPage = data.pageNumber + 1;
    this.dom.$pagination.isDisabled = false;
    this.dom.$pagination.pageSizeOptions = data.pageSizeOptions;
    this.dom.$pagination.translations = {
      pageSizeText: translateFn('Items per page'),
      pageCountText: translateFn(`of ${totalNumberOfPages} pages`),
    };
    const pageStart = Math.max(data.pageNumber * data.pageSize + 1, 0);
    const pageEnd = Math.min(data.pageNumber * data.pageSize + data.pageSize, data.itemsTotal);
    this.dom.$summary.text = `${pageStart} - ${pageEnd} of ${data.itemsTotal} items`; // add translateFn
  }

  public setTranslatedStrings() {
    this.dom.$noResultsMessage.innerText = translateFn('No results found.');
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$gridStates);
    this.setElementClass(this.dom.$content, ElementClass.HIDDEN);
    this.setTemplateState(this.dom.$gridStates, TemplateState.LOADING);
    this.setElementClass(this.dom.$noResultsContainer, ElementClass.HIDDEN);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$content);
    this.resetTemplateState(this.dom.$gridStates);
  }

  public handleGridVisibility(itemsTotal: number) {
    if (itemsTotal === 0) {
      this.setElementClass(this.dom.$content, ElementClass.HIDDEN);
      this.removeElementClass(this.dom.$noResultsContainer, ElementClass.HIDDEN);
    } else {
      this.removeElementClass(this.dom.$content, ElementClass.HIDDEN);
      this.setElementClass(this.dom.$noResultsContainer, ElementClass.HIDDEN);
    }
  }
}
